import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  Home,
  Loader2,
  MessageCircle,
  Check,
  ChevronLeft,
  ChevronRight,
  X,
  Car,
  Shirt,
  UtensilsCrossed,
  Wind,
  Layers,
  Building,
  Dumbbell,
  Waves,
  Wifi,
  Dog,
  Snowflake,
  Tv,
  Lock,
  Mail,
  Bath,
  BedDouble,
  Calendar,
  CreditCard,
  DollarSign,
  Maximize2,
  Square,
} from "lucide-react";
import listingModel from "../../lib/firebase/listingModel";
import PublicLeoChat from "../../components/vacancy/PublicLeoChat";
import FeedbackWidget from "../../components/FeedbackWidget";
import { MobileWidgetProvider } from "../../context/MobileWidgetContext";
import SEOHead from "../../components/seo/SEOHead";
import { APP_NAME } from "../../helpers/constants";
import { trackEvent } from "../../helpers/mixpanel";

/**
 * AIVacancyLandingPage - AI-First Public Listing
 *
 * Core differentiator: Embedded AI Receptionist that answers questions 24/7
 * before guiding qualified leads to schedule a tour.
 *
 * Layout:
 * - Mobile: FAB (Floating Action Button) opens chat overlay
 * - Desktop: Chat sidebar always visible alongside listing details
 */
const AIVacancyLandingPage = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for demo flag in URL
  const searchParams = new URLSearchParams(location.search);
  const isDemo = searchParams.get("demo") === "true";
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [leoOpenSignal, setLeoOpenSignal] = useState(0);
  const [isFeedbackWidgetOpen, setIsFeedbackWidgetOpen] = useState(false);

  // Fetch listing data
  const {
    data: listing,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["ai-listing", listingId],
    queryFn: async () => {
      const data = await listingModel.getPublicListingById(listingId);
      return data;
    },
    enabled: !!listingId,
    retry: 1,
  });

  // Track page view
  useEffect(() => {
    if (listing) {
      listingModel.incrementViewCount(listingId).catch(console.error);

      trackEvent("ai_vacancy_page_viewed", {
        listing_id: listingId,
        address: listing.address,
        rent_amount: listing.rentAmount,
      });
    }
  }, [listing, listingId]);

  // Get images - use listing photos if available, otherwise use demo placeholders if demo flag is set
  // Define this early so it can be used in functions below
  const images = useMemo(() => {
    if (listing?.photos && listing.photos.length > 0) {
      return listing.photos;
    }
    if (isDemo) {
      return [
        "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1600&h=900&q=85&fit=crop",
        "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&h=900&q=85&fit=crop",
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&h=900&q=85&fit=crop",
        "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&h=900&q=85&fit=crop",
        "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1600&h=900&q=85&fit=crop",
        "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1600&h=900&q=85&fit=crop",
      ];
    }
    return [];
  }, [isDemo, listing?.photos]);

  // SEO metadata should include real listing photos, and demo placeholder images when in demo mode.
  // Must be declared here (before any early returns) to keep hook order stable.
  const seoListingData = useMemo(() => {
    if (!listing) return null;
    return {
      ...listing,
      photos: images,
      metaImageUrl: listing.metaImageUrl || images?.[0] || null,
    };
  }, [listing, images]);

  // Image carousel controls
  const nextImage = () => {
    if (images.length > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentImageIndex((prev) =>
        prev === images.length - 1 ? 0 : prev + 1
      );
      setTimeout(() => setIsTransitioning(false), 400);
    }
  };

  const prevImage = () => {
    if (images.length > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentImageIndex((prev) =>
        prev === 0 ? images.length - 1 : prev - 1
      );
      setTimeout(() => setIsTransitioning(false), 400);
    }
  };

  // Touch handlers for mobile drag/swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextImage();
    } else if (isRightSwipe) {
      prevImage();
    }
  };

  // Reset image index when images change
  useEffect(() => {
    if (images.length > 0) {
      setCurrentImageIndex(0);
    }
  }, [images.length]);

  // Lightbox controls
  const openLightbox = (index) => {
    if (images.length === 0) return;
    // Update hero carousel to show the same image
    setCurrentImageIndex(index);
    setLightboxIndex(index);
    setLightboxOpen(true);
    setIsFeedbackWidgetOpen(false);
    trackEvent("listing_lightbox_opened", {
      listing_id: listingId,
      image_index: index,
      total_images: listing?.photos?.length || 0,
    });
  };

  const closeLightbox = useCallback(() => {
    if (lightboxOpen) {
      trackEvent("listing_lightbox_closed", {
        listing_id: listingId,
        image_index: lightboxIndex,
      });
    }
    setLightboxOpen(false);
  }, [lightboxOpen, lightboxIndex, listingId]);

  const nextLightboxImage = useCallback(() => {
    if (images.length === 0) return;
    setLightboxIndex((prev) => {
      const nextIndex = prev === images.length - 1 ? 0 : prev + 1;
      // Sync hero carousel with lightbox
      setCurrentImageIndex(nextIndex);
      return nextIndex;
    });
  }, [images]);

  const prevLightboxImage = useCallback(() => {
    if (images.length === 0) return;
    setLightboxIndex((prev) => {
      const prevIndex = prev === 0 ? images.length - 1 : prev - 1;
      // Sync hero carousel with lightbox
      setCurrentImageIndex(prevIndex);
      return prevIndex;
    });
  }, [images]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") nextLightboxImage();
      if (e.key === "ArrowLeft") prevLightboxImage();
      if (e.key === "Escape") closeLightbox();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, nextLightboxImage, prevLightboxImage, closeLightbox]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "Contact for pricing";
    return `$${amount.toLocaleString()}`;
  };

  // Format address
  const formatAddress = () => {
    if (!listing) return "";
    const parts = [
      listing.address,
      listing.city,
      listing.state,
      listing.zipCode,
    ].filter(Boolean);
    return parts.join(", ");
  };

  // Map amenity names to appropriate icons
  const getAmenityIcon = (amenityName) => {
    const normalizedName = amenityName.toLowerCase().trim();

    const iconMap = {
      parking: Car,
      garage: Car,
      laundry: Shirt,
      washer: Shirt,
      dryer: Shirt,
      dishwasher: UtensilsCrossed,
      "air conditioning": Wind,
      ac: Wind,
      "a/c": Wind,
      "hardwood floors": Layers,
      hardwood: Layers,
      balcony: Building,
      patio: Building,
      deck: Building,
      gym: Dumbbell,
      fitness: Dumbbell,
      pool: Waves,
      "swimming pool": Waves,
      wifi: Wifi,
      internet: Wifi,
      "pet friendly": Dog,
      "pets allowed": Dog,
      pets: Dog,
      heating: Snowflake,
      heat: Snowflake,
      tv: Tv,
      television: Tv,
      cable: Tv,
      security: Lock,
      doorman: Lock,
      elevator: Building,
      mail: Mail,
    };

    // Try exact match first
    if (iconMap[normalizedName]) {
      return iconMap[normalizedName];
    }

    // Try partial match
    for (const [key, Icon] of Object.entries(iconMap)) {
      if (normalizedName.includes(key) || key.includes(normalizedName)) {
        return Icon;
      }
    }

    // Default to Check icon if no match found
    return Check;
  };

  // Loading state
  if (isLoading) {
    return (
      <LoadingContainer>
        <Loader2 className="animate-spin" size={48} />
        <LoadingText>Loading property details...</LoadingText>
      </LoadingContainer>
    );
  }

  // Error/Not Found state
  if (isError || !listing) {
    return (
      <>
        <Helmet>
          <title>Listing Not Available | {APP_NAME}</title>
        </Helmet>
        <NotFoundContainer>
          <NotFoundCard>
            <NotFoundIcon>
              <Home size={64} />
            </NotFoundIcon>
            <NotFoundTitle>This listing is no longer available</NotFoundTitle>
            <NotFoundText>
              The property you're looking for may have been rented or removed
              from our listings.
            </NotFoundText>
            <BackButton onClick={() => navigate("/")}>
              Return to Homepage
            </BackButton>
          </NotFoundCard>
        </NotFoundContainer>
      </>
    );
  }

  // Get current image for hero section (using currentImageIndex)
  const currentImage = images[currentImageIndex] || images[0];
  // All images should appear in gallery
  const galleryImages = images;

  return (
    <>
      <SEOHead listingData={seoListingData} listingId={listingId} />

      <PageContainer>
        {/* Hero Image Section */}
        {images.length > 0 && (
          <HeroSection
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <CarouselContainer>
              <CarouselTrack
                style={{
                  transform: `translateX(-${currentImageIndex * 100}%)`,
                }}
              >
                {images.map((image, index) => (
                  <CarouselSlide key={index}>
                    <HeroImage
                      src={image}
                      alt={`${listing.address} - Photo ${index + 1}`}
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                      onClick={() => openLightbox(index)}
                    />
                  </CarouselSlide>
                ))}
              </CarouselTrack>
            </CarouselContainer>
            <HeroOverlay />
            {images.length > 1 && (
              <>
                <CarouselButton
                  direction="left"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                >
                  <ChevronLeft size={24} />
                </CarouselButton>
                <CarouselButton
                  direction="right"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                >
                  <ChevronRight size={24} />
                </CarouselButton>
                <ImageCounter>
                  {currentImageIndex + 1} / {images.length}
                </ImageCounter>
                <ViewGalleryButton
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox(currentImageIndex);
                  }}
                >
                  <Maximize2 size={20} />
                  View All {images.length} Photos
                </ViewGalleryButton>
              </>
            )}
          </HeroSection>
        )}

        {/* Main Content Area */}
        <ContentWrapper>
          {/* Property Header - Key Info */}
          <PropertyHeader>
            <HeaderTop>
              <HeaderLeft>
                <HeadlineRow>
                  <Headline>{listing.headline || listing.address}</Headline>
                  <LogoBadgeLink to="/">
                    <LogoBadge>
                      <img src="/logo.png" alt={APP_NAME} />
                    </LogoBadge>
                  </LogoBadgeLink>
                </HeadlineRow>
                <AddressLine>{formatAddress()}</AddressLine>
                {listing.description && (
                  <Description>{listing.description}</Description>
                )}
              </HeaderLeft>
              <PriceTag>
                <ForRentLabel>
                  {listing.listingType === "rent_to_own"
                    ? "Rent-to-Own"
                    : "For Rent"}
                </ForRentLabel>
                {listing.listingType === "rent_to_own" && listing.rentToOwn ? (
                  <>
                    <PriceRow>
                      <Price>
                        {formatCurrency(
                          listing.rentToOwn.monthlyPayment || listing.rentAmount
                        )}
                      </Price>
                      <PriceLabel>/month</PriceLabel>
                    </PriceRow>
                    {listing.rentToOwn.downPayment && (
                      <DownPaymentLabel>
                        Down: {formatCurrency(listing.rentToOwn.downPayment)}
                      </DownPaymentLabel>
                    )}
                  </>
                ) : (
                  <PriceRow>
                    <Price>{formatCurrency(listing.rentAmount)}</Price>
                    <PriceLabel>/month</PriceLabel>
                  </PriceRow>
                )}
                <ApplyNowButton
                  type="button"
                  onClick={() => {
                    trackEvent("public_listing_apply_now_clicked", {
                      listing_id: listingId,
                    });
                    setLeoOpenSignal((s) => s + 1);
                  }}
                >
                  Apply now
                </ApplyNowButton>
              </PriceTag>
            </HeaderTop>

            {/* Key Stats */}
            <KeyStats>
              {listing.bedrooms && (
                <StatItem>
                  <BedDouble size={20} />
                  <StatValue>{listing.bedrooms}</StatValue>
                  <StatLabel>Bed{listing.bedrooms !== 1 ? "s" : ""}</StatLabel>
                </StatItem>
              )}
              {listing.bathrooms && (
                <StatItem>
                  <Bath size={20} />
                  <StatValue>{listing.bathrooms}</StatValue>
                  <StatLabel>
                    Bath{listing.bathrooms !== 1 ? "s" : ""}
                  </StatLabel>
                </StatItem>
              )}
              {listing.sqft && (
                <StatItem>
                  <Square size={20} />
                  <StatValue>{listing.sqft.toLocaleString()}</StatValue>
                  <StatLabel>sq ft</StatLabel>
                </StatItem>
              )}
              {listing.availableDate && (
                <StatItem>
                  <Calendar size={20} />
                  <StatValue>
                    {new Date(listing.availableDate).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric" }
                    )}
                  </StatValue>
                  <StatLabel>Available</StatLabel>
                </StatItem>
              )}
            </KeyStats>
          </PropertyHeader>

          {/* Image Gallery Grid */}
          {galleryImages.length > 0 && (
            <GallerySection>
              <SectionTitle>Photo Gallery</SectionTitle>
              <GalleryGrid>
                {galleryImages.map((image, index) => (
                  <GalleryImageContainer
                    key={index}
                    onClick={() => openLightbox(index)}
                  >
                    <GalleryImage
                      src={image}
                      alt={`${listing.address} - Photo ${index + 1}`}
                      loading="lazy"
                    />
                    <ImageOverlay>
                      <Maximize2 size={24} />
                    </ImageOverlay>
                  </GalleryImageContainer>
                ))}
              </GalleryGrid>
            </GallerySection>
          )}

          {/* Property Details Grid */}
          <DetailsGrid>
            {/* Amenities */}
            {listing.amenities && listing.amenities.length > 0 && (
              <DetailsCard>
                <SectionTitle>Amenities & Features</SectionTitle>
                <AmenitiesList>
                  {listing.amenities.map((amenity, index) => {
                    const IconComponent = getAmenityIcon(amenity);
                    return (
                      <AmenityItem key={index}>
                        <IconComponent size={18} />
                        {amenity}
                      </AmenityItem>
                    );
                  })}
                </AmenitiesList>
              </DetailsCard>
            )}

            {/* Property Requirements */}
            {listing.rules && (
              <DetailsCard>
                <SectionTitle>Property Requirements</SectionTitle>
                <RulesList>
                  {listing.listingType === "rent_to_own" &&
                    listing.rentToOwn && (
                      <>
                        {!listing.rentToOwn.creditCheckRequired && (
                          <RuleItem>
                            <Check size={18} />
                            No Credit Check Required - Income-based
                            qualification
                          </RuleItem>
                        )}
                        {listing.rentToOwn.incomeBasedQualification && (
                          <RuleItem>
                            <Check size={18} />
                            Qualification based on monthly income
                          </RuleItem>
                        )}
                      </>
                    )}
                  {listing.rules.petsAllowed !== undefined && (
                    <RuleItem>
                      <Check size={18} />
                      {listing.rules.petsAllowed ? "Pets Allowed" : "No Pets"}
                      {listing.rules.petDeposit &&
                        ` (${formatCurrency(
                          listing.rules.petDeposit
                        )} deposit)`}
                    </RuleItem>
                  )}
                  {listing.rules.minCreditScore &&
                    listing.listingType !== "rent_to_own" && (
                      <RuleItem>
                        <Check size={18} />
                        Credit Score {listing.rules.minCreditScore}+
                      </RuleItem>
                    )}
                  {listing.rules.minIncomeMultiplier && (
                    <RuleItem>
                      <Check size={18} />
                      Income {listing.rules.minIncomeMultiplier}x{" "}
                      {listing.listingType === "rent_to_own"
                        ? "Monthly Payment"
                        : "Rent"}{" "}
                      Required
                    </RuleItem>
                  )}
                </RulesList>
              </DetailsCard>
            )}
          </DetailsGrid>

          {/* Rent-to-Own Financing Details */}
          {listing.listingType === "rent_to_own" && listing.rentToOwn && (
            <RentToOwnSection>
              <RentToOwnBadge>
                <Home style={{ filter: "invert(1)" }} size={20} />
                Rent-to-Own
              </RentToOwnBadge>

              {listing.rentToOwn.financingDescription && (
                <FinancingDescription>
                  {listing.rentToOwn.financingDescription}
                </FinancingDescription>
              )}

              <FinancingDetailsGrid>
                <FinancingItem>
                  <FinancingLabel>Down Payment</FinancingLabel>
                  <FinancingValueStyled>
                    {formatCurrency(listing.rentToOwn.downPayment)}
                  </FinancingValueStyled>
                </FinancingItem>

                <FinancingItem>
                  <FinancingLabel>Monthly Payment</FinancingLabel>
                  <FinancingValueStyled>
                    {formatCurrency(listing.rentToOwn.monthlyPayment)}
                  </FinancingValueStyled>
                  {!listing.rentToOwn.taxesInsuranceIncluded && (
                    <FinancingNote>
                      (taxes & insurance not included)
                    </FinancingNote>
                  )}
                </FinancingItem>

                <FinancingItem>
                  <FinancingLabel>Term</FinancingLabel>
                  <FinancingValueStyled>
                    {listing.rentToOwn.term} years
                  </FinancingValueStyled>
                </FinancingItem>

                <FinancingItem>
                  <FinancingLabel>Interest Rate</FinancingLabel>
                  <FinancingValueStyled>
                    {listing.rentToOwn.interestRate}%
                  </FinancingValueStyled>
                </FinancingItem>
              </FinancingDetailsGrid>

              <FinancingHighlights>
                {!listing.rentToOwn.creditCheckRequired && (
                  <HighlightItem>
                    <Check size={18} color="#10b981" />
                    <span>Credit check: Not required</span>
                  </HighlightItem>
                )}
                {listing.rentToOwn.moveInDate && (
                  <HighlightItem>
                    <Check size={18} color="#10b981" />
                    <span>
                      Move-in: Ready in{" "}
                      {new Date(
                        listing.rentToOwn.moveInDate
                      ).toLocaleDateString("en-US", {
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </HighlightItem>
                )}
                {listing.rentToOwn.toursAvailable && (
                  <HighlightItem>
                    <Check size={18} color="#10b981" />
                    <span>Tours: Available by request</span>
                  </HighlightItem>
                )}
              </FinancingHighlights>
            </RentToOwnSection>
          )}

          {/* Financial Details - Only show for regular rentals, not rent-to-own */}
          {listing.listingType !== "rent_to_own" && (
            <FinancialSection>
              <FinancialGrid>
                {listing.rentAmount && (
                  <FinancialItem>
                    <FinancialLabel>Monthly Rent</FinancialLabel>
                    <FinancialValue>
                      {formatCurrency(listing.rentAmount)}
                    </FinancialValue>
                  </FinancialItem>
                )}
                {listing.deposit && listing.deposit > 0 && (
                  <FinancialItem>
                    <FinancialLabel>Security Deposit</FinancialLabel>
                    <FinancialValue>
                      {formatCurrency(listing.deposit)}
                    </FinancialValue>
                  </FinancialItem>
                )}
              </FinancialGrid>
            </FinancialSection>
          )}
        </ContentWrapper>

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <LightboxOverlay onClick={closeLightbox}>
            <LightboxContent onClick={(e) => e.stopPropagation()}>
              <LightboxCloseButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeLightbox();
                }}
              >
                <X size={24} />
              </LightboxCloseButton>
              <LightboxImage
                key={lightboxIndex}
                src={images[lightboxIndex]}
                alt={`${listing.address} - Photo ${lightboxIndex + 1}`}
              />
              {images.length > 1 && (
                <>
                  <LightboxNavButton
                    type="button"
                    direction="left"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      prevLightboxImage();
                    }}
                  >
                    <ChevronLeft size={32} />
                  </LightboxNavButton>
                  <LightboxNavButton
                    type="button"
                    direction="right"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      nextLightboxImage();
                    }}
                  >
                    <ChevronRight size={32} />
                  </LightboxNavButton>
                  <LightboxCounter>
                    {lightboxIndex + 1} / {images.length}
                  </LightboxCounter>
                </>
              )}
            </LightboxContent>
          </LightboxOverlay>
        )}

        {/* Mobile Widget Provider - Manages widget visibility on mobile */}
        <MobileWidgetProvider>
          {/* Public Leo Chat Widget - Bottom Right */}
          <PublicLeoChat
            listingData={listing}
            listingId={listingId}
            isVisible={!!listing && !lightboxOpen}
            openSignal={leoOpenSignal}
          />

          {/* Feedback Widget - Bottom Left (works for logged-in and non-logged-in users) */}
          <FeedbackWidget
            isVisible={!!listing && !lightboxOpen}
            isOpen={isFeedbackWidgetOpen}
            onToggle={() => setIsFeedbackWidgetOpen((prev) => !prev)}
          />
        </MobileWidgetProvider>
      </PageContainer>
    </>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  position: relative;
`;

const HeroSection = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
  background: #f0f0f0;
  cursor: pointer;
  touch-action: pan-y pinch-zoom;

  @media (max-width: 768px) {
    height: 350px;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* Ensure only one image is visible at a time */
  isolation: isolate;
`;

const CarouselTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  /* Ensure smooth sliding without showing adjacent images */
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const CarouselSlide = styled.div`
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  height: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  position: relative;
  /* Ensure each slide takes exactly 100% width */
  box-sizing: border-box;
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: opacity 0.4s ease-in-out;
  image-rendering: auto;
  backface-visibility: hidden;
  transform: translateZ(0);
  will-change: opacity;
  user-select: none;
  -webkit-user-drag: none;
  display: block;
  min-width: 100%;
  max-width: 100%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: transparent;
  pointer-events: none;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  ${(props) => props.direction}: 1rem;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;

  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${(props) => props.direction}: 0.5rem;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
  z-index: 10;

  @media (max-width: 768px) {
    bottom: 0.75rem;
    left: 0.75rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const ViewGalleryButton = styled.button`
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  color: var(--color-text);
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    bottom: 1rem;
    right: 1rem;
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  position: relative;
  z-index: 5;

  @media (max-width: 1024px) {
    padding: 1.5rem 1rem;
  }
`;

const PropertyHeader = styled.div`
  background: var(--color-bg-secondary);
  padding: 2.5rem;
  border-radius: 20px;
  border: 1px solid var(--color-border-light);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }

  @media (max-width: 768px) {
    padding: 1.75rem;
    border-radius: 16px;
    margin-bottom: 1.5rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left !important;
  align-items: flex-start;

  @media (min-width: 641px) {
    text-align: left !important;
    align-items: flex-start !important;
  }
`;

const HeadlineRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;

  @media (max-width: 640px) {
    gap: 0.5rem;
    align-items: center;
  }
`;

const HeadlineContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem;
`;

const BedBathInfo = styled.span`
  font-size: 2rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-text);
  line-height: 1.3;
  letter-spacing: -0.02em;

  @media (max-width: 640px) {
    font-size: 1.5rem;
  }
`;

const Headline = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-text);
  margin: 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  display: inline;
  text-align: left !important;
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;

  @media (min-width: 641px) {
    text-align: left !important;
  }

  @media (max-width: 640px) {
    font-size: 1.5rem;
    line-height: 1.25;
  }
`;

const AddressLine = styled.div`
  color: var(--color-text-secondary);
  font-size: 1.125rem;
  font-family: var(--font-secondary);
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;

const LogoBadgeLink = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  /* Keep the logo button pinned to the top when the headline wraps to multiple lines */
  align-self: flex-start;
  margin-top: 5px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    margin-top: 2px;
  }

  &:hover {
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }
`;

const LogoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  padding: 4px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 640px) {
    width: 28px;
    height: 28px;
  }
`;

const PriceTag = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  white-space: nowrap;

  @media (max-width: 640px) {
    flex-direction: column; /* Keep "For Rent" above the price on mobile */
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const ApplyNowButton = styled.button`
  margin-top: 0.65rem;
  padding: 0.6rem 0.95rem;
  border-radius: 12px;
  border: 1px solid rgba(44, 95, 45, 0.22);
  background: var(--color-primary-dark);
  color: #ffffff;
  font-weight: 800;
  font-family: var(--font-primary);
  letter-spacing: -0.01em;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;
  align-self: flex-end;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(44, 95, 45, 0.22);
    background: var(--color-primary);
  }

  &:active {
    transform: translateY(0px);
  }

  @media (max-width: 640px) {
    width: 100%;
    align-self: center;
    white-space: normal;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const ForRentLabel = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-primary-dark);
  background: rgba(44, 95, 45, 0.08);
  border: 1px solid rgba(44, 95, 45, 0.18);
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  align-self: flex-end;

  @media (max-width: 640px) {
    font-size: 0.7rem;
    padding: 0.24rem 0.5rem;
    align-self: center;
  }
`;

const Price = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-primary-dark);
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 640px) {
    font-size: 2rem;
    line-height: 1.1;
  }
`;

const PriceLabel = styled.span`
  font-size: 1.0625rem;
  color: var(--color-text-secondary);
  font-weight: 500;
  font-family: var(--font-secondary);
  letter-spacing: 0.01em;

  @media (max-width: 640px) {
    font-size: 0.9375rem;
    margin-top: 0; /* No top margin since it's on the same line */
  }
`;

const KeyStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 2rem;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid var(--color-border-light);

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    padding-top: 1.5rem;
    margin-top: 1.5rem;

    /* Center the 3rd item if it's alone in its row (when there are 3 or 5 items) */
    > *:nth-child(3):nth-last-child(1),
    > *:nth-child(3):nth-last-child(3) {
      grid-column: 1 / -1;
      justify-self: center;
      max-width: 140px;
    }
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;

  svg {
    color: var(--color-primary);
    stroke-width: 2;
    flex-shrink: 0;
  }
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-text);
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const GallerySection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  font-family: var(--font-primary);
  color: var(--color-text);
  margin: 0 0 1.5rem 0;
  line-height: 1.4;
  letter-spacing: -0.01em;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryImageContainer = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: #f0f0f0;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${GalleryImageContainer}:hover & {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;

  svg {
    color: white;
    stroke: white;
  }

  ${GalleryImageContainer}:hover & {
    opacity: 1;
  }
`;

const Description = styled.p`
  font-size: 1.0625rem;
  line-height: 1.75;
  font-family: var(--font-secondary);
  color: var(--color-text);
  margin: 1rem 0 0 0;
  white-space: pre-wrap;
  font-weight: 400;
  text-align: left !important;

  @media (min-width: 641px) {
    text-align: left !important;
  }

  @media (max-width: 640px) {
    font-size: 1rem;
    margin-top: 0.75rem;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DetailsCard = styled.div`
  background: var(--color-bg-secondary);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const AmenitiesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AmenityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text);
  font-size: 1rem;
  font-family: var(--font-secondary);
  font-weight: 500;
  line-height: 1.5;

  svg {
    color: var(--color-primary);
    flex-shrink: 0;
  }
`;

const RulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (max-width: 768px) {
    align-items: flex-start;
    text-align: left !important;
  }
`;

const RuleItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-text);
  font-weight: 500;
  font-family: var(--font-secondary);
  font-size: 1rem;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    color: var(--color-primary);
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
    text-align: left !important;
    width: 100%;
  }
`;

const FinancialSection = styled.div`
  background: var(--color-primary-glow);
  border: 1px solid var(--color-primary);
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FinancialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FinancialItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FinancialLabel = styled.div`
  font-size: 0.9375rem;
  font-weight: 500;
  font-family: var(--font-secondary);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FinancialValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-primary-dark);
`;

const DownPaymentLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: 0.5rem;
  text-align: right;

  @media (max-width: 640px) {
    text-align: center;
  }
`;

const RentToOwnSection = styled.div`
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #10b981;
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
`;

const RentToOwnBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #10b981;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const FinancingDescription = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  color: #065f46;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const FinancingDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const FinancingItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const FinancingLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const FinancingValueStyled = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #065f46;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const FinancingNote = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const FinancingHighlights = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9375rem;
  color: #065f46;
`;

const RentToOwnNote = styled.p`
  font-size: 0.9375rem;
  font-style: italic;
  color: #059669;
  margin-top: 1rem;
  text-align: center;
`;

// Lightbox Styles
const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  cursor: pointer;
  pointer-events: auto;
`;

const LightboxContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: default;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
`;

const LightboxCloseButton = styled.button`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 2;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: white;

  svg {
    color: white;
    stroke: white;
  }

  &:hover {
    background: transparent;
    transform: scale(1.1);
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
  }
`;

const LightboxNavButton = styled.button`
  position: fixed;
  top: 50%;
  ${(props) => props.direction}: 24px;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  z-index: 2;
  pointer-events: auto;

  svg {
    color: white;
    stroke: white;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    ${(props) => props.direction}: 12px;
    width: 44px;
    height: 44px;
  }
`;

const LightboxCounter = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  z-index: 2;

  @media (max-width: 768px) {
    bottom: 16px;
    font-size: 0.875rem;
  }
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const LoadingText = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
`;

const NotFoundContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--color-background);
  position: relative;

  /* Subtle gradient background */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 30% 20%,
      rgba(44, 95, 45, 0.03) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const NotFoundCard = styled.div`
  background: var(--color-bg-secondary);
  border-radius: 24px;
  padding: 4rem 3rem;
  max-width: 600px;
  width: 100%;
  box-shadow:
    0 20px 40px var(--shadow-light),
    0 8px 16px rgba(44, 95, 45, 0.08);
  border: 1px solid var(--color-border-light);
  text-align: center;
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 640px) {
    padding: 3rem 2rem;
    border-radius: 20px;
  }
`;

const NotFoundIcon = styled.div`
  color: var(--color-primary);
  margin-bottom: 2rem;
  display: flex;
  justify-content: center;
  opacity: 0.9;

  svg {
    filter: drop-shadow(0 4px 8px rgba(44, 95, 45, 0.15));
  }
`;

const NotFoundTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 700;
  font-family: var(--font-primary);
  color: var(--color-text);
  margin: 0 0 1rem 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
  overflow-wrap: anywhere;

  @media (max-width: 640px) {
    font-size: 1.75rem;
  }
`;

const NotFoundText = styled.p`
  font-size: 1.125rem;
  font-family: var(--font-secondary);
  color: var(--color-text-secondary);
  margin: 0 0 2.5rem 0;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  overflow-wrap: anywhere;
  hyphens: auto; /* Allows hyphenation if needed */

  @media (max-width: 640px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const BackButton = styled.button`
  padding: 0.875rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-secondary);
  color: white;
  background: var(--gradient-primary);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 4px 12px rgba(44, 95, 45, 0.2),
    0 2px 4px rgba(44, 95, 45, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    background: var(--gradient-primary-dark);
    transform: translateY(-2px);
    box-shadow:
      0 8px 20px rgba(44, 95, 45, 0.3),
      0 4px 8px rgba(44, 95, 45, 0.15);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.2);
  }

  @media (max-width: 640px) {
    padding: 0.75rem 1.75rem;
    font-size: 0.9375rem;
    width: 100%;
    max-width: 280px;
  }
`;

export default AIVacancyLandingPage;
